import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, ChevronDown, Ear, Loader2, Mic2, RefreshCcw } from "lucide-react";
import { lyricsVerdict } from "@/lib/studioApi";

const TONES = {
  good: "border-emerald-400/30 bg-emerald-500/10 text-emerald-100",
  warn: "border-amber-400/35 bg-amber-500/10 text-amber-100",
  bad: "border-rose-400/40 bg-rose-500/10 text-rose-100",
  pending: "border-white/10 bg-white/[0.04] text-white/70",
  error: "border-white/10 bg-white/[0.04] text-white/60",
};

/** Lyrics Lock result: verdict, lines that may have changed, what the AI heard, and the two ways forward. */
export default function LyricsCheckPanel({ versionId, check, recordHref, recordLabel = "Record it myself", onRecheck, onRegenerate, rechecking }) {
  const [open, setOpen] = useState(false);
  const v = lyricsVerdict(check);
  if (!v) return null;
  const flagged = check?.flagged || [];
  const warn = v.tone === "warn" || v.tone === "bad" || flagged.length > 0;
  const Icon = v.tone === "pending" ? Loader2 : v.tone === "good" && !flagged.length ? CheckCircle2 : AlertTriangle;
  return (
    <div data-testid={`lyrics-check-${versionId}`} data-verdict={check?.verdict || check?.status} className={`rounded-2xl border px-4 py-3 ${TONES[flagged.length && v.tone === "good" ? "warn" : v.tone]}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-[13px] font-medium">
          <Icon className={`h-4 w-4 ${v.tone === "pending" ? "animate-spin" : ""}`} />
          <span data-testid={`lyrics-check-label-${versionId}`}>{v.label}</span>
          {flagged.length ? <span className="text-white/60">· {flagged.length} line{flagged.length > 1 ? "s" : ""} may differ</span> : null}
        </p>
        {check?.status === "done" || check?.status === "error" ? (
          <button type="button" data-testid={`lyrics-check-toggle-${versionId}`} onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.16em] text-white/60 hover:text-white">
            Details <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        ) : null}
      </div>
      {check?.status === "error" ? <p className="mt-1 text-[12px] text-white/55">{check.error}</p> : null}
      {warn && check?.status === "done" ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {onRegenerate ? (
            <button type="button" data-testid={`lyrics-warn-regenerate-${versionId}`} onClick={onRegenerate} className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.12] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-white hover:bg-white/[0.2]">
              <RefreshCcw className="h-3 w-3" /> Regenerate
            </button>
          ) : null}
          {recordHref ? (
            <Link to={recordHref} data-testid={`lyrics-warn-record-${versionId}`} className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.12] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-white hover:bg-white/[0.2]">
              <Mic2 className="h-3 w-3" /> {recordLabel}
            </Link>
          ) : null}
        </div>
      ) : null}
      {open ? (
        <div data-testid={`lyrics-check-details-${versionId}`} className="mt-3 space-y-3 border-t border-white/10 pt-3 text-[12px]">
          {flagged.length ? (
            <div>
              <p className="mb-1 font-mono text-[10px] uppercase tracking-[0.16em] text-white/55">Lines ELPM couldn't clearly hear</p>
              <ul className="space-y-1">{flagged.map((f, i) => <li key={i} className="flex justify-between gap-3"><span>{f.text}</span><span className="shrink-0 font-mono text-white/45">{Math.round(f.score * 100)}%</span></li>)}</ul>
            </div>
          ) : null}
          {check?.heard_text ? (
            <div>
              <p className="mb-1 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-white/55"><Ear className="h-3 w-3" /> What the AI heard</p>
              <p className="max-h-40 overflow-y-auto whitespace-pre-wrap leading-relaxed text-white/65">{check.heard_text}</p>
            </div>
          ) : null}
          {check?.source === "vocal" ? <p data-testid={`lyrics-check-source-${versionId}`} className="text-[11px] text-emerald-200/70">Checked on the isolated vocal (music removed) for accuracy.</p> : null}
          <p className="text-[11px] text-white/40">AI listening is an estimate — sung words, accents and ad-libs can blur. Trust your ears first.</p>
          <button type="button" data-testid={`lyrics-recheck-${versionId}`} onClick={onRecheck} disabled={rechecking} className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-white/60 hover:text-white disabled:opacity-40">
            {rechecking ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCcw className="h-3 w-3" />} Check again
          </button>
        </div>
      ) : null}
    </div>
  );
}
